import PropTypes from 'prop-types'
import React from 'react'
import Notification from 'components/Notification'
import LearnErrorBoundary from 'components/LearnErrorBoundary'
import LessonList from 'experienceComponents/LessonList'
import RecommendedAdvisoryActivities from 'experienceComponents/RecommendedAdvisoryActivities'
import UnitOverview from 'experienceComponents/UnitOverview'
import UnitTabSet from 'experienceComponents/UnitTabSet'
import VideoCallout from 'experienceComponents/VideoCallout'
import { isFlagOn } from 'utils/featureFlags'
import { isK5Url } from 'utils/routing/routeHelpers'
import {
  AcknowledgementsLink,
  UnitBodyBackground,
  UnitBodyContent,
  UnitComponentWrapper,
  UnitOverviewBox,
  UnitTopContent,
} from './component.styles'
import UnitComponentEvents from './events'
import { getData } from './data'
import { PAGE_DISPLAY_TYPE } from './constants'
import { LMSUnitType, UnitType } from './types'

const UnitComponent = props => {
  const {
    acknowledgements,
    match,
    navigationEvent,
    onSelectUnit,
    productName,
    unit,
    unitId,
    lmsUnit,
  } = props
  if (isK5Url() && isFlagOn(['feature_LEARN-20164-k5-site-selection'])) {
    console.log('[DEBUG] UnitComponent rendering K5 in program flex')
  }

  if (!unit) {
    return (
      <Notification color="danger">
        <h3>{`Unit ${match?.params?.unit ?? 'unknown'} not found`}</h3>
      </Notification>
    )
  }

  const {
    advisoryActivities,
    lessonStartingIndex,
    unitVideo,
    unitName,
    lmsLessons,
    unitTabs,
  } = getData({ contentUnit: unit, currentUnitId: unitId, lmsUnit })
  const showAdditionalResources = advisoryActivities.size === 3
  const gradeTheme = match.params?.course
    ? match.params.course.replace('-', '')
    : ''
  // `pageDisplayType` is a Contentful Boolean field with custom labels
  // ("lessonPage" = true, "lessonAccordion"/unset = false). `true` renders the
  // new link-summary cards; anything else renders the legacy accordion tree.
  const isLessonPage = unit.pageDisplayType === PAGE_DISPLAY_TYPE.PAGE
  const isLessonAccordion = !isLessonPage
  const lessonSummaries = isLessonPage
    ? Array.isArray(unit.linkSummaries)
      ? unit.linkSummaries
      : Array.isArray(unit.linkSummariesCollection)
      ? unit.linkSummariesCollection
      : []
    : []

  const acknowledgementsLink = acknowledgements && (
    <AcknowledgementsLink
      acknowledgementsDataNOTCONNECTED={acknowledgements}
      dataTestId="acknowledgements-link"
      onClick={UnitComponentEvents.onClickAcknowledgements({
        match,
        acknowledgements,
        navigationEvent,
      })}
    >
      Acknowledgements
    </AcknowledgementsLink>
  )

  return (
    <LearnErrorBoundary>
      {isLessonAccordion ? (
        <>
          <UnitComponentWrapper animation={'fadeIn'}>
            <UnitTabSet
              ariaControls={'unit-overviewbox'}
              match={match}
              onSelect={onSelectUnit}
              selectedUnitSlug={unitId}
              units={unitTabs}
            />
            <UnitOverviewBox id="unit-overviewbox" role={'tabpanel'}>
              <UnitOverview
                contentfulItem={unit}
                dataTestId={`unit-overview-${unitId}`}
                tabIndex={0}
              />
              {unitVideo && (
                <VideoCallout
                  dataTestId={`video-callout-${unitId}`}
                  id="video-callout"
                  locale={'en-US'}
                  videoDisplayTitle={unitVideo.displayTitle}
                  videoId={unitVideo.id}
                  videoLength={unitVideo.time}
                  videoThumbnailUrl={unitVideo.videoReference.posterImage?.url}
                />
              )}
            </UnitOverviewBox>
            <LessonList
              contentfulLessons={unit.lessons}
              dataTestId="lesson-list"
              displayAsAccordion={isLessonAccordion}
              gradeTheme={gradeTheme}
              lessonStartingIndex={lessonStartingIndex}
              lessons={lmsLessons}
              match={match}
              unitResources={unit.unitResources}
            />
            {showAdditionalResources && (
              <RecommendedAdvisoryActivities
                contentfulItem={unit}
                match={match}
                productName={productName}
                unitName={unitName}
              />
            )}
          </UnitComponentWrapper>
          {acknowledgementsLink}
        </>
      ) : (
        <UnitComponentWrapper animation={'fadeIn'}>
          <UnitTopContent>
            <UnitTabSet
              ariaControls={'unit-overviewbox'}
              match={match}
              onSelect={onSelectUnit}
              selectedUnitSlug={unitId}
              units={unitTabs}
            />
          </UnitTopContent>
          <UnitBodyBackground $hasSideCallout={Boolean(unitVideo)}>
            <UnitOverviewBox id="unit-overviewbox" role={'tabpanel'}>
              <UnitOverview
                contentfulItem={unit}
                dataTestId={`unit-overview-${unitId}`}
                tabIndex={0}
              />
              {unitVideo && (
                <VideoCallout
                  dataTestId={`video-callout-${unitId}`}
                  id="video-callout"
                  locale={'en-US'}
                  videoDisplayTitle={unitVideo.displayTitle}
                  videoId={unitVideo.id}
                  videoLength={unitVideo.time}
                  videoThumbnailUrl={unitVideo.videoReference.posterImage?.url}
                />
              )}
            </UnitOverviewBox>
            <UnitBodyContent>
              <LessonList
                contentfulLessons={unit.lessons}
                dataTestId="lesson-list"
                displayAsAccordion={isLessonAccordion}
                gradeTheme={gradeTheme}
                lessonStartingIndex={lessonStartingIndex}
                lessonSummaries={lessonSummaries}
                lessons={lmsLessons}
                match={match}
                unitResources={unit.unitResources}
              />
              {showAdditionalResources && (
                <RecommendedAdvisoryActivities
                  contentfulItem={unit}
                  match={match}
                  productName={productName}
                  unitName={unitName}
                />
              )}
            </UnitBodyContent>
          </UnitBodyBackground>
          {acknowledgementsLink}
        </UnitComponentWrapper>
      )}
    </LearnErrorBoundary>
  )
}

UnitComponent.propTypes = {
  acknowledgements: PropTypes.object,
  lmsUnit: LMSUnitType,
  match: PropTypes.object.isRequired,
  navigationEvent: PropTypes.func,
  onSelectUnit: PropTypes.func.isRequired,
  productName: PropTypes.string,
  unit: UnitType,
  unitId: PropTypes.string,
}

UnitComponent.displayName = 'UnitComponent'

export default UnitComponent
